import { Link } from "react-router-dom"
import React, { useState, useEffect, useMemo } from "react"
import axios from "axios"
import Modal from "@mui/material/Modal"
import Box from "@mui/material/Box"

import "./Users.sass"
import UsersTable from "./UsersTable"

import { toast } from "react-toastify"

const initialGroup = "all"
const initialStatus = 2

function getRoleActualName(name) {
  switch (name) {
    case "Administração":
      return "admin"
    case "Gestão de Contratos":
      return "contracts"
    case "Gestão de Faturamento":
      return "billing"
  }
}

function Users() {
  const [openModal, setOpen] = useState(false)
  const handleOpenModal = () => setOpen(true)
  const handleCloseModal = () => {
    setOpen(false)
    setIsSent(false)
  }

  const [openContractModal, setContractModalOpen] = useState(false)
  const handleOpenContractModal = () => setContractModalOpen(true)
  const handleCloseContractModal = () => setContractModalOpen(false)

  const [data, setData] = useState([])
  const [selectedIds, setSelectedIds] = useState([])
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)
  const [statusFilter, setStatusFilter] = useState(initialStatus)
  const [groupFilter, setGroupFilter] = useState(initialGroup)

  const [localUser, setLocalUser] = useState(false)

  const [email, setEmail] = useState(null)
  const [contract, setContract] = useState(null)

  const [possibleContracts, setPossibleContracts] = useState([])

  const [isSent, setIsSent] = useState(false)

  const filterUsersTable = (event) => {
    setGroupFilter(event.target.value)
  }

  const filterUsersStatus = (event) => {
    setStatusFilter(event.target.value)
  }

  const onVerifyUser = async (e) => {
    e.preventDefault()

    if (email == null || email == "") {
      toast.info("Preencha o email", {
        theme: "colored",
      })

      return
    }

    const id = toast.info("Verificando usuário...", {
      theme: "colored",
    })

    const token = localStorage.getItem("token")

    const api = axios.create({
      baseURL: process.env.REACT_APP_API_URL,
      headers: {
        Authorization: token,
      },
    })

    api
      .get("user/verify/" + email)
      .then((response) => {
        toast.update(id, {
          render: "Usuário encontrado",
          isLoading: false,
          type: "success",
          autoClose: 3000,
        })

        let contracts = response.data

        contracts.sort((a, b) => {
          if (a.status < b.status) {
            return -1
          }
          if (a.status > b.status) {
            return 1
          }
          return 0
        })

        setPossibleContracts(contracts)

        handleOpenContractModal()
        handleCloseModal()
      })
      .catch((error) => {
        const errorMsg = error.response
          ? error.response.data.error
          : "Erro interno no servidor, contate um administrador"

        toast.update(id, {
          render: errorMsg,
          isLoading: false,
          type: "error",
        })

        console.log("Error verifying user:")
        console.log(error)
      })
  }

  const onConfirmSubmission = async (e) => {
    e.preventDefault()

    setEmail(null)
    setContract(null)

    setContractModalOpen(false)
  }

  const onSubmitUser = async (e) => {
    e.preventDefault()

    if (email == null || email == "" || contract == null || contract == "") {
      toast.info("Preencha os campos", {
        theme: "colored",
      })

      return
    }

    const id = toast.info("Criando usuário...", {
      theme: "colored",
    })

    const body = {
      email,
      contractId: contract,
    }

    const token = localStorage.getItem("token")

    const api = axios.create({
      baseURL: process.env.REACT_APP_API_URL,
      headers: {
        Authorization: token,
      },
    })

    api
      .post("user/create", body)
      .then((response) => {
        toast.update(id, {
          render: "Usuário criado",
          isLoading: false,
          type: "success",
          autoClose: 3000,
        })

        setIsSent(true)
      })
      .catch((error) => {
        const errorMsg = error.response
          ? error.response.data.error
          : "Erro interno no servidor, contate um administrador"

        toast.update(id, {
          render: errorMsg,
          isLoading: false,
          type: "error",
        })

        console.log("Error creating user:")
        console.log(error)
      })
  }

  const filteredData = useMemo(() => {
    const normalizedStatus = Number(statusFilter)
    let result = data

    if (groupFilter === "admin") {
      result = result.filter((user) => user.isAdmin === true)
    }

    if (normalizedStatus === 2) {
      return result
    }

    const shouldBeActive = normalizedStatus === 0
    return result.filter((user) => (user.isActive !== false) === shouldBeActive)
  }, [data, statusFilter, groupFilter])

  const fetchUsers = () => {
    const token = localStorage.getItem("token")

    const api = axios.create({
      baseURL: process.env.REACT_APP_API_URL,
      headers: {
        Authorization: token,
      },
    })

    api
      .get("user/users")
      .then((response) => {
        const sortedData = response.data.sort((a, b) =>
          a.name.localeCompare(b.name)
        )

        setData(sortedData)
      })
      .catch((error) => {
        toast.error(error.response.data.error, {
          theme: "colored",
        })
        console.log(error.response)
      })
  }

  const toggleUser = (userId) => {
    setSelectedIds((current) =>
      current.includes(userId)
        ? current.filter((id) => id !== userId)
        : [...current, userId]
    )
  }

  const toggleAllVisible = () => {
    const visibleIds = filteredData.map((user) => user.id)
    const allSelected = visibleIds.every((id) => selectedIds.includes(id))

    if (allSelected) {
      setSelectedIds((current) =>
        current.filter((id) => !visibleIds.includes(id))
      )
      return
    }

    setSelectedIds((current) => [...new Set([...current, ...visibleIds])])
  }

  const updateSelectedStatus = async (isActive) => {
    if (selectedIds.length === 0 || isUpdatingStatus) {
      return
    }

    setIsUpdatingStatus(true)

    const token = localStorage.getItem("token")
    const actionLabel = isActive ? "ativando" : "desativando"
    const toastId = toast.info(`Aguarde, ${actionLabel} usuários...`, {
      theme: "colored",
    })

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}user/update-status`,
        { ids: selectedIds, isActive },
        { headers: { Authorization: token } }
      )

      const count = response.data.count
      const skippedSelf = response.data.skippedSelf
      const successLabel = isActive ? "ativado" : "desativado"
      const plural = count === 1 ? "usuário" : "usuários"

      toast.update(toastId, {
        render: skippedSelf
          ? `${count} ${plural} ${successLabel}. O seu usuário não foi desativado.`
          : `${count} ${plural} ${successLabel} com sucesso`,
        isLoading: false,
        type: "success",
        autoClose: 3000,
      })

      setSelectedIds([])
      fetchUsers()
    } catch (error) {
      toast.update(toastId, {
        render:
          error.response?.data?.error ||
          "Ocorreu um erro ao atualizar os usuários",
        isLoading: false,
        type: "error",
      })
    } finally {
      setIsUpdatingStatus(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [openModal, openContractModal])

  return (
    <>
      <Modal
        open={openContractModal}
        onClose={handleCloseContractModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box className="box-modal form-wrapper">
          {isSent ? (
            <form onSubmit={onConfirmSubmission}>
              <div className="form-header">
                <span className="font text-4">Criação de usuário</span>
              </div>
              <div className="form-content">
                <label htmlFor="contract" className="font text-2">
                  Usuário criado com sucesso, foi enviado um e-mail com as
                  informações de login a <strong>{email}</strong>
                </label>
                <input type="submit" value="Ok" className="action-btn" />
              </div>
            </form>
          ) : (
            <form onSubmit={onSubmitUser}>
              <div className="form-header">
                <span className="font text-4">Contrato</span>
              </div>
              <div className="form-content">
                <label htmlFor="contract" className="font text-2">
                  Selecione um contrato
                </label>
                <select
                  name="contract"
                  id="contract"
                  onChange={(event) => {
                    setContract(event.target.value)
                  }}
                >
                  <option value="">Selecione um contrato</option>
                  {possibleContracts.map((contract) => (
                    <option
                      key={contract.contractid}
                      value={contract.contractid}
                    >
                      {contract.status + " - " + contract.contractname}
                    </option>
                  ))}
                </select>
                <input
                  type="submit"
                  value="Criar usuário"
                  className="action-btn"
                />
              </div>
            </form>
          )}
          <div className="aside-img-div">
            <img src="/assets/images/logo.png" className="bottom-form-logo" />
          </div>
        </Box>
      </Modal>
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box className="box-modal form-wrapper">
          <form onSubmit={onVerifyUser}>
            <div className="form-header">
              <span className="font text-4">Adicionar Usuário</span>
            </div>
            <div className="form-content">
              <label htmlFor="email" className="font text-2">
                Email
              </label>
              <input
                type="text"
                name="email"
                id="email"
                onChange={(event) => {
                  setEmail(event.target.value)
                }}
              />
              <input type="submit" value="Validar" className="action-btn" />
            </div>
          </form>
          <div className="aside-img-div">
            <img src="/assets/images/logo.png" className="bottom-form-logo" />
          </div>
        </Box>
      </Modal>
      <div id="content-wrapper">
        <div id="top-actions">
          <div className="action" id="addContractBtn" onClick={handleOpenModal}>
            <img src="/assets/icons/add.svg" alt="" className="icon" />
            <span className="font text-2">Adicionar usuário</span>
          </div>
          <div
            className={`action ${
              selectedIds.length === 0 || isUpdatingStatus ? "disabled" : ""
            }`}
            onClick={() => updateSelectedStatus(true)}
          >
            <span className="font text-2">Ativar</span>
          </div>
          <div
            className={`action ${
              selectedIds.length === 0 || isUpdatingStatus ? "disabled" : ""
            }`}
            onClick={() => updateSelectedStatus(false)}
          >
            <span className="font text-2">Desativar</span>
          </div>
        </div>
        <div id="table-header">
          <span className="text-4 font">
            <strong>Usuários</strong>
          </span>
          <div id="filter-options">
            <div className="filter-item">
              <span className="text-1 font">Grupo</span>
              <select
                name="contract-billing-day"
                id="contract-billing-day"
                value={groupFilter}
                onChange={filterUsersTable}
              >
                <option value={"all"}>Todos</option>
                <option value={"admin"}>admin</option>
              </select>
            </div>
            <div className="filter-item">
              <span className="text-1 font">Status</span>
              <select
                name="contract-status"
                id="contract-status"
                value={statusFilter}
                onChange={filterUsersStatus}
              >
                <option value={0}>Ativos</option>
                <option value={1}>Inativos</option>
                <option value={2}>Todos</option>
              </select>
            </div>
          </div>
        </div>
        <UsersTable
          data={filteredData}
          selectedIds={selectedIds}
          onToggleUser={toggleUser}
          onToggleAll={toggleAllVisible}
        />
      </div>
    </>
  )
}

export default Users
